'use client';
import { useEffect, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';

/**
 * useRealtimeRefresh
 *
 * Subscribes to Supabase Realtime postgres_changes for one or more tables
 * and calls `onRefresh` whenever any INSERT / UPDATE / DELETE fires on those tables.
 *
 * Usage:
 *   useRealtimeRefresh(['attendance', 'enrollments'], fetchData);
 */
export function useRealtimeRefresh(
  tables: string | string[],
  onRefresh: () => void,
  enabled = true
) {
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;

  // Normalise: accept a single string or an array
  const tableList: string[] = Array.isArray(tables)
    ? tables
    : tables
    ? [tables]
    : [];

  const tableKey = tableList.join(',');

  useEffect(() => {
    if (!enabled || tableList.length === 0) return;

    const supabase = createClient();
    const channelName = `realtime-refresh-${tableList.join('-')}-${Math.random().toString(36).slice(2, 7)}`;
    const channel = supabase.channel(channelName);

    tableList.forEach((table) => {
      channel.on(
        'postgres_changes' as any,
        { event: '*', schema: 'public', table },
        () => {
          onRefreshRef.current();
        }
      );
    });

    channel.subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, tableKey]);
}
